"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_PRIMARY, HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  CompactTextArea,
  DraftPanel,
  FormHeader,
} from "@/components/tools/academic-correspondence/kit";
import { ReviewerCommentCard } from "@/components/tools/academic-correspondence/reviewer-comment-card";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { countPlaceholders } from "@/lib/tool-kit/correspondence/compose";
import {
  buildResponseLetter,
  canMergeUp,
  commentNumber,
  EDITOR_GROUP,
  mergeWithPrevious,
  numberComments,
  parseReviewerComments,
  RESPONSE_EDITOR_TITLES,
  RESPONSE_LETTER_BANK,
  type ResponseEditorTitle,
  type ReviewerComment,
  removeComment,
  type Stance,
  updateComment,
} from "@/lib/tool-kit/correspondence/response-to-reviewers";

/** English strings. A translated content file overrides these through `ui`. */
export const RESPONSE_TO_REVIEWERS_GENERATOR_STRINGS = {
  formHeading: "Your manuscript",
  tryExample: "Try an example",
  clear: "Clear",
  editorTitleLabel: "Editor's title",
  editorTitleOptions: {
    dr: "Dr.",
    drNoStop: "Dr (no full stop, British style)",
    professor: "Professor",
    none: "No title",
  },
  editorNameLabel: "Editor's family name (optional)",
  editorNamePlaceholder: "Osei",
  editorNameHint: "Leave blank for “Dear Editor”.",
  journalLabel: "Journal (optional)",
  journalPlaceholder: "Educational Research Letters",
  titleLabel: "Manuscript title",
  titlePlaceholder: "Peer tutoring and reading attainment in secondary schools",
  manuscriptIdLabel: "Manuscript ID (optional)",
  manuscriptIdPlaceholder: "ERL-2026-0412",
  nameLabel: "Corresponding author",
  namePlaceholder: "Hana Kobayashi",
  soleAuthorLabel: "I am the only author",
  trackedLabel: "I am also uploading a tracked-changes version",
  mainChangesLabel: "Main changes, one per line (optional)",
  mainChangesPlaceholder:
    "Added the power calculation to the Methods\nAdded school-level results (Table S3)",
  commentsHeading: "Reviewer comments",
  pasteLabel:
    "Paste the reviewers' comments — headings such as “Reviewer 1” and numbered points are detected",
  pastePlaceholder:
    "Reviewer #1:\n1. The power analysis is not reported…\n2. …\n\nReviewer #2:\n…",
  split: "Split into comments",
  splitHint:
    "Splitting again replaces the comments below, including any responses you have written.",
  commentsFound:
    "one:{count} comment found — check the split, then choose a response for each.|other:{count} comments found — check the split, then choose a response for each.",
  noComments:
    "Paste the reviewers' comments above and choose “Split into comments”. Nothing is uploaded — the splitting happens in your browser.",
  cardLabel: "{group} · Comment {id}",
  commentTextLabel: "Comment, as the reviewer wrote it",
  mergeUp: "Merge with previous",
  remove: "Remove",
  stanceLabel: "Your response",
  stanceOptions: {
    agree: "Agree — revised as suggested",
    partial: "Partly agree — revised in part",
    disagree: "Respectfully disagree — no change",
    thanks: "General remark — thank the reviewer",
  },
  changeLabel: "What you changed",
  changeOptionalLabel: "Anything you changed (optional)",
  changePlaceholder: "We have added the power calculation to the Methods.",
  rationaleLabel: "Your reasoning",
  rationalePlaceholder:
    "The test was scored by external markers who did not know which classes received tutoring.",
  pageLabel: "Page",
  pagePlaceholder: "7",
  linesLabel: "Lines",
  linesPlaceholder: "112–118",
  explainedHint:
    "Page and lines only if you added an explanation to the manuscript.",
  newTextLabel: "Revised text to quote (optional)",
  newTextPlaceholder: "The new sentence or passage, as it now reads",
  draftHeading: "Your response letter",
  copy: "Copy",
  copyPlain: "Copy plain text",
  copied: "Copied!",
  download: "Download .txt",
  downloadFilename: "response-to-reviewers.txt",
  placeholdersLeft:
    "one:{count} placeholder left to fill — shown in [brackets]|other:{count} placeholders left to fill — shown in [brackets]",
  noPlaceholders:
    "No placeholders left. Check every page and line number against the final revised file.",
  example: {
    editorTitle: "dr",
    editorName: "Osei",
    journal: "Educational Research Letters",
    title:
      "Peer tutoring and reading attainment in secondary schools: a cluster randomised trial",
    manuscriptId: "ERL-2026-0412",
    name: "Hana Kobayashi",
    mainChanges:
      "Added the power calculation to the Methods\nAdded school-level results (Table S3)\nExpanded the comparison with earlier peer-tutoring studies",
    comments:
      "Reviewer #1:\nThe authors report a randomised trial of a peer-tutoring programme in 14 secondary schools. The study is well designed and clearly written, but several points need attention.\n\n1. The power analysis is not reported. Please state how the target sample size was determined.\n2. The reading test was administered by teachers who knew which classes received tutoring. Could this have biased the results?\n3. The discussion would benefit from comparing the effect size with those of other peer-tutoring programmes.\n\nReviewer #2:\nMajor comments\n1. Please report the results separately for each school, as the programme may have worked differently in different settings.\nMinor comments\n2. Table 2: please give the units for the attendance variable.",
  },
  exampleStances: ["thanks", "agree", "disagree", "agree", "partial", "agree"],
  examplePages: ["", "6", "8", "14", "11", "10"],
  exampleLines: ["", "98–104", "140–143", "280–292", "210–215", ""],
  exampleChanges: [
    "",
    "We have added the power calculation to the Methods: with 14 schools and 1,120 students, the trial had 80% power to detect an effect size of 0.25 at α = 0.05.",
    "",
    "We now compare our effect size with the pooled estimates reported in two recent meta-analyses of peer tutoring.",
    "We have added the school-level results as a supplementary table (Table S3) and describe the range of effects in the Results.",
    "Attendance is now given as the percentage of sessions attended.",
  ],
  exampleRationales: [
    "",
    "",
    "The reading test was scored by external markers who did not know which classes had received tutoring, so teachers' knowledge of allocation could not affect the scores.",
    "",
    "With about 80 students per school, school-by-school significance tests would be underpowered and could mislead readers, so we report the school-level estimates descriptively.",
    "",
  ],
  ...RESPONSE_LETTER_BANK,
} as const;

type Strings = typeof RESPONSE_TO_REVIEWERS_GENERATOR_STRINGS;

interface LetterForm {
  editorTitle: ResponseEditorTitle;
  editorName: string;
  journal: string;
  title: string;
  manuscriptId: string;
  name: string;
  soleAuthor: boolean;
  tracked: boolean;
  mainChanges: string;
}

const EMPTY_FORM: LetterForm = {
  editorTitle: "dr",
  editorName: "",
  journal: "",
  title: "",
  manuscriptId: "",
  name: "",
  soleAuthor: false,
  tracked: false,
  mainChanges: "",
};

/** The example review, split, with the example responses laid over it in order. */
function exampleComments(strings: Strings): ReviewerComment[] {
  return parseReviewerComments(strings.example.comments).map(
    (comment, index) => ({
      ...comment,
      stance: (strings.exampleStances[index] ?? "agree") as Stance,
      page: strings.examplePages[index] ?? "",
      lines: strings.exampleLines[index] ?? "",
      change: strings.exampleChanges[index] ?? "",
      rationale: strings.exampleRationales[index] ?? "",
    })
  );
}

function ManuscriptFields({
  form,
  update,
  strings,
}: {
  form: LetterForm;
  update: <K extends keyof LetterForm>(key: K, value: LetterForm[K]) => void;
  strings: Strings;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="rtr-editor-title"
          label={strings.editorTitleLabel}
          onChange={(value: ResponseEditorTitle) =>
            update("editorTitle", value)
          }
          options={RESPONSE_EDITOR_TITLES.map((key) => ({
            value: key,
            label: strings.editorTitleOptions[key],
          }))}
          value={form.editorTitle}
        />
        <TextField
          hint={strings.editorNameHint}
          id="rtr-editor-name"
          label={strings.editorNameLabel}
          onChange={(value) => update("editorName", value)}
          placeholder={strings.editorNamePlaceholder}
          value={form.editorName}
        />
        <TextField
          id="rtr-title"
          label={strings.titleLabel}
          onChange={(value) => update("title", value)}
          placeholder={strings.titlePlaceholder}
          value={form.title}
        />
        <TextField
          id="rtr-journal"
          label={strings.journalLabel}
          onChange={(value) => update("journal", value)}
          placeholder={strings.journalPlaceholder}
          value={form.journal}
        />
        <TextField
          id="rtr-manuscript-id"
          label={strings.manuscriptIdLabel}
          onChange={(value) => update("manuscriptId", value)}
          placeholder={strings.manuscriptIdPlaceholder}
          value={form.manuscriptId}
        />
        <TextField
          id="rtr-name"
          label={strings.nameLabel}
          onChange={(value) => update("name", value)}
          placeholder={strings.namePlaceholder}
          value={form.name}
        />
      </div>
      <div className="space-y-2">
        <Checkbox
          checked={form.soleAuthor}
          label={strings.soleAuthorLabel}
          onChange={(checked) => update("soleAuthor", checked)}
        />
        <Checkbox
          checked={form.tracked}
          label={strings.trackedLabel}
          onChange={(checked) => update("tracked", checked)}
        />
      </div>
      <CompactTextArea
        id="rtr-main-changes"
        label={strings.mainChangesLabel}
        onChange={(value) => update("mainChanges", value)}
        placeholder={strings.mainChangesPlaceholder}
        rows={3}
        value={form.mainChanges}
      />
    </>
  );
}

export default function ResponseToReviewersGeneratorWidget() {
  const strings = useToolUi(RESPONSE_TO_REVIEWERS_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [form, setForm] = useState<LetterForm>(EMPTY_FORM);
  const [pasted, setPasted] = useState("");
  const [comments, setComments] = useState<ReviewerComment[]>([]);

  const update = <K extends keyof LetterForm>(key: K, value: LetterForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const numbering = useMemo(() => numberComments(comments), [comments]);
  const paragraphs = useMemo(
    () => buildResponseLetter({ ...form, comments }, strings, locale),
    [form, comments, strings, locale]
  );
  const blanks = countPlaceholders(paragraphs);
  const status =
    blanks > 0
      ? tPlural(strings.placeholdersLeft, locale, blanks, {
          count: fmt(blanks),
        })
      : strings.noPlaceholders;

  function fillExample() {
    const { editorTitle, comments: review, ...rest } = strings.example;
    setForm({
      ...EMPTY_FORM,
      ...rest,
      editorTitle: editorTitle as ResponseEditorTitle,
      tracked: true,
    });
    setPasted(review);
    setComments(exampleComments(strings));
  }

  function clearAll() {
    setForm(EMPTY_FORM);
    setPasted("");
    setComments([]);
  }

  function groupLabel(group: string): string {
    return group === EDITOR_GROUP
      ? strings.editorHeading
      : t(strings.reviewerHeading, { n: group });
  }

  return (
    <>
      <section className="space-y-5">
        <FormHeader
          clearLabel={strings.clear}
          exampleLabel={strings.tryExample}
          heading={strings.formHeading}
          onClear={clearAll}
          onExample={fillExample}
        />
        <ManuscriptFields form={form} strings={strings} update={update} />
      </section>

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.commentsHeading}</h2>
        <TextAreaField
          id="rtr-paste"
          label={strings.pasteLabel}
          onChange={setPasted}
          placeholder={strings.pastePlaceholder}
          prose
          rows={8}
          value={pasted}
        />
        <div className="flex flex-wrap items-center gap-3">
          <button
            className={BUTTON_PRIMARY}
            disabled={pasted.trim() === ""}
            onClick={() => setComments(parseReviewerComments(pasted))}
            type="button"
          >
            {strings.split}
          </button>
          <p className={HINT}>{strings.splitHint}</p>
        </div>
        <p aria-live="polite" className={HINT}>
          {comments.length > 0
            ? tPlural(strings.commentsFound, locale, comments.length, {
                count: fmt(comments.length),
              })
            : ""}
        </p>
        {comments.length === 0 ? (
          <Notice>{strings.noComments}</Notice>
        ) : (
          <ol className="space-y-4">
            {comments.map((comment) => (
              <ReviewerCommentCard
                canMerge={canMergeUp(comments, comment.id)}
                comment={comment}
                key={comment.id}
                label={t(strings.cardLabel, {
                  group: groupLabel(comment.group),
                  id: commentNumber(comment, numbering, strings),
                })}
                onChange={(patch) =>
                  setComments((current) =>
                    updateComment(current, comment.id, patch)
                  )
                }
                onMerge={() =>
                  setComments((current) =>
                    mergeWithPrevious(current, comment.id)
                  )
                }
                onRemove={() =>
                  setComments((current) => removeComment(current, comment.id))
                }
                strings={strings}
              />
            ))}
          </ol>
        )}
      </section>

      <DraftPanel
        filename={strings.downloadFilename}
        heading={strings.draftHeading}
        paragraphs={paragraphs}
        status={status}
        strings={strings}
      />
    </>
  );
}
